import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";

@Component({
  selector: "jni-pdf-viewer-page",
  templateUrl: "./pdf-viewer-page.component.html",
  styleUrls: ["./pdf-viewer-page.component.scss"],
  imports: [CommonModule, NgxExtendedPdfViewerModule],
})
export class PdfViewerPageComponent implements OnInit {
  /** sessionStorage key prefix used for cross-tab base64 handoff. */
  private static readonly STORAGE_PREFIX = "pdf-viewer:";

  @Input() base64pdf = "";
  @Input() fileNameToDownload = "document";
  @Input() isModal = false;
  @Output() closePdfStackerEvent = new EventEmitter<void>();

  /** True when opened via the /pdf-viewer/:key route (no modal chrome). */
  standalone = false;

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    const key = this.route.snapshot.paramMap.get("key");
    if (key) {
      this.standalone = true;
      this.loadFromSessionStorage(key);
    }
  }

  /**
   * Stashes a base64 PDF in sessionStorage under a random key and opens
   * the standalone PDF viewer route in a new tab. Call this directly
   * from a click handler so the browser treats the window.open as a
   * user gesture (avoids popup-blocker issues).
   *
   * @returns the newly opened window, or null if the popup was blocked.
   */
  static openInNewTab(base64: string, fileName = "document"): Window | null {
    const key = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const storageKey = `${PdfViewerPageComponent.STORAGE_PREFIX}${key}`;
    sessionStorage.setItem(storageKey, JSON.stringify({ base64, fileName }));
    const win = window.open(`/pdf-viewer/${key}`, "_blank");
    if (win) {
      // The child tab has already inherited a copy of sessionStorage,
      // so we can safely delete the opener's copy to avoid piling up.
      sessionStorage.removeItem(storageKey);
    }
    return win;
  }

  closePdfStacker(): void {
    this.closePdfStackerEvent.emit();
  }

  private loadFromSessionStorage(key: string): void {
    const storageKey = `${PdfViewerPageComponent.STORAGE_PREFIX}${key}`;
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return;
    }
    try {
      const parsed = JSON.parse(raw) as {
        base64?: string;
        fileName?: string;
      };
      this.base64pdf = parsed.base64 ?? "";
      this.fileNameToDownload = parsed.fileName?.trim()
        ? parsed.fileName
        : "document";
    } finally {
      // Free the storage - the PDF is now in memory.
      sessionStorage.removeItem(storageKey);
    }
  }
}
